import { FormattedMessage } from 'react-intl';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  AGREEMENT_TYPE_OPTIONS,
  NEW_OR_ONGOING_OPTIONS,
  HOUSEHOLD_STATUS_OPTIONS,
} from '../constants';

export const getFormFields = (
  messages,
  intl,
  { unitResidentOptions = [], voucherMonthMoment, isModifying = false },
) => [
  {
    variant: 'singleSelect',
    name: 'agreementType',
    label: <FormattedMessage {...messages.agreementTypeLabel} />,
    placeholder: intl.formatMessage(messages.agreementTypePlaceholder),
    options: AGREEMENT_TYPE_OPTIONS,
    fullWidth: true,
    disabled: isModifying,
    rules: { required: true },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'newOrOngoing',
    label: <FormattedMessage {...messages.newOrOngoingLabel} />,
    placeholder: intl.formatMessage(messages.newOrOngoingPlaceholder),
    options: NEW_OR_ONGOING_OPTIONS,
    fullWidth: true,
    hidden: isModifying,
    disabled: isModifying,
    rules: { required: true },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'householdStatus',
    label: <FormattedMessage {...messages.householdStatusLabel} />,
    placeholder: intl.formatMessage(messages.householdStatusPlaceholder),
    options: HOUSEHOLD_STATUS_OPTIONS,
    fullWidth: true,
    disabled: isModifying,
    rules: {
      required: ({ getValues }) => getValues('agreementType') !== 'O',
    },
    watchFields: ['agreementType'],
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'singleSelect',
    name: 'unitResident',
    label: <FormattedMessage {...messages.unitResidentLabel} />,
    placeholder: intl.formatMessage(messages.unitResidentPlaceholder),
    options: unitResidentOptions,
    fullWidth: true,
    searchable: true,
    disabled: isModifying
      ? true
      : ({ getValues }) =>
          getValues('householdStatus') !== 'CURRENT_RESIDENT' &&
          getValues('householdStatus') !== 'PRIOR_RESIDENT',
    watchFields: ['agreementType', 'householdStatus'],
    rules: {
      required: ({ getValues }) => getValues('agreementType') !== 'O',
    },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'date',
    name: 'agreementDate',
    label: <FormattedMessage {...messages.agreementDateLabel} />,
    fullWidth: true,
    maxDate: voucherMonthMoment ?? undefined,
    disabled: isModifying,
    rules: {
      required: true,
      validate: (value) => {
        if (!value) return true;
        const max = voucherMonthMoment;
        if (!max || !max.toDate) return true;
        const d =
          typeof value === 'string'
            ? new Date(value)
            : value && value.toDate
            ? value.toDate()
            : value;
        const maxDate = max.toDate();
        return (
          (d && d <= maxDate) || intl.formatMessage(messages.agreementDateError)
        );
      },
    },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'currency',
    name: 'agreementAmount',
    label: <FormattedMessage {...messages.agreementAmountLabel} />,
    fullWidth: true,
    onlyWholeNumbers: true,
    rules: {
      required: true,
      min: 1,
    },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'currency',
    name: 'beginningBalance',
    label: <FormattedMessage {...messages.beginningBalanceLabel} />,
    fullWidth: true,
    onlyWholeNumbers: true,
    disabled: isModifying
      ? true
      : ({ getValues }) => getValues('newOrOngoing') === 'NEW',
    InputLabelProps: {
      shrink: true,
      TooltipProps: {
        title: intl.formatMessage(messages.beginningBalanceTooltip),
        placement: 'right',
        children: <AlertInfoIcon fontSize="small" />,
      },
    },
    rules: {
      required: ({ getValues }) => getValues('newOrOngoing') === 'ONGOING',
      min: 0,
    },
    watchFields: ['newOrOngoing'],
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'currency',
    name: 'totalPayment',
    label: <FormattedMessage {...messages.totalPaymentLabel} />,
    fullWidth: true,
    onlyWholeNumbers: true,
    disabled: ({ getValues }) => getValues('agreementType') === 'N',
    InputLabelProps: {
      shrink: true,
      TooltipProps: {
        title: intl.formatMessage(messages.totalPaymentTooltip),
        placement: 'right',
        children: <AlertInfoIcon fontSize="small" />,
      },
    },
    watchFields: ['agreementType'],
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'currency',
    name: 'agreementChangeAmount',
    label: <FormattedMessage {...messages.agreementChangeAmountLabel} />,
    fullWidth: true,
    onlyWholeNumbers: true,
    // Only rows that were carried over can be modified, so a modify is always an
    // ongoing agreement and this field must stay editable -- AC3 is the user
    // overriding the auto-filled value. Deferring to newOrOngoing here made the
    // field permanently disabled in modify mode, because that field is `hidden`
    // then and so the lookup never returns 'ONGOING' (ENG-26976).
    disabled: isModifying
      ? false
      : ({ getValues }) => getValues('newOrOngoing') !== 'ONGOING',
    InputLabelProps: {
      shrink: true,
      TooltipProps: {
        title: intl.formatMessage(messages.agreementChangeAmountTooltip),
        placement: 'right',
        children: <AlertInfoIcon fontSize="small" />,
      },
    },
    // No lower bound: reducing the Agreement Amount produces a NEGATIVE change
    // amount, which is the whole point of the decrease scenario (ENG-26976).
    // What still guards the numbers is Agreement Amount >= 1 and the
    // endingBalance rule below, both mirrored on the server.
    rules: {
      required: ({ getValues }) => getValues('newOrOngoing') === 'ONGOING',
    },
    watchFields: ['newOrOngoing'],
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'currency',
    name: 'endingBalance',
    label: <FormattedMessage {...messages.endingBalanceLabel} />,
    fullWidth: true,
    onlyWholeNumbers: true,
    readOnly: true,
    InputLabelProps: {
      shrink: true,
      TooltipProps: {
        title: intl.formatMessage(messages.endingBalanceTooltip),
        placement: 'right',
        children: <AlertInfoIcon fontSize="small" />,
      },
    },
    rules: {
      validate: (value) => {
        const n = Number(value);
        if (value === '' || value == null) return true;
        if (Number.isNaN(n) || n !== Math.floor(n))
          return intl.formatMessage(messages.currencyWholeNumberError);
        if (n < 0)
          return intl.formatMessage(messages.endingBalanceNegativeError);
        return true;
      },
    },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'currency',
    name: 'amountRetained',
    label: <FormattedMessage {...messages.amountRetainedLabel} />,
    fullWidth: true,
    onlyWholeNumbers: true,
    disabled: ({ getValues }) => getValues('agreementType') !== 'T',
    watchFields: ['agreementType', 'totalPayment'],
    InputLabelProps: {
      shrink: true,
      TooltipProps: {
        title: intl.formatMessage(messages.amountRetainedTooltip),
        placement: 'right',
        children: <AlertInfoIcon fontSize="small" />,
      },
    },
    rules: {
      min: 0,
      required: ({ getValues }) => getValues('agreementType') === 'T',
      validate: (value, getValues) => {
        if (getValues('agreementType') !== 'T') return true;
        if (value === '' || value == null) return true;
        const retained = Number(value);
        if (Number.isNaN(retained) || !Number.isFinite(retained)) {
          return intl.formatMessage(messages.currencyWholeNumberError);
        }
        const retainedFloored = Math.floor(retained);
        const totalRaw = getValues('totalPayment');
        const total = Number(totalRaw ?? 0);
        const totalSafe = Number.isFinite(total) ? Math.max(0, total) : 0;
        const maxTwentyPct = Math.floor(totalSafe * 0.2);
        if (retainedFloored > totalSafe || retainedFloored > maxTwentyPct) {
          return intl.formatMessage(messages.amountRetainedError);
        }
        return true;
      },
    },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'currency',
    name: 'amountRequested',
    label: <FormattedMessage {...messages.amountRequestedLabel} />,
    fullWidth: true,
    onlyWholeNumbers: true,
    readOnly: true,
    InputLabelProps: {
      shrink: true,
      TooltipProps: {
        title: intl.formatMessage(messages.amountRequestedTooltip),
        placement: 'right',
        children: <AlertInfoIcon fontSize="small" />,
      },
    },
    // No lower bound here either. Amount Requested is derived as
    // change - totalPayment + retained, so it is negative for most carried-over
    // rows (no change, just a payment). Because isSaveDisabled fails on ANY
    // invalid field, a min:0 on this read-only display field could block Save
    // on a perfectly valid row.
    rules: {
      required: ({ getValues }) => getValues('agreementType') === 'T',
    },
    GridProps: { xs: 12, sm: 6 },
  },
  {
    variant: 'text',
    name: 'agreementId',
    label: <FormattedMessage {...messages.agreementIdLabel} />,
    placeholder: intl.formatMessage(messages.agreementIdPlaceholder),
    fullWidth: true,
    inputProps: { maxLength: 14 },
    disabled: isModifying
      ? true
      : ({ getValues }) => getValues('newOrOngoing') !== 'ONGOING',
    InputLabelProps: {
      shrink: true,
      TooltipProps: {
        title: intl.formatMessage(messages.agreementIdTooltip),
        placement: 'right',
        children: <AlertInfoIcon fontSize="small" />,
      },
    },
    watchFields: ['newOrOngoing'],
    rules: {
      required: ({ getValues }) => getValues('newOrOngoing') === 'ONGOING',
      maxLength: 14,
      pattern: /^[A-Za-z0-9]+$/,
    },
    GridProps: { xs: 12, sm: 6 },
  },
];
